import React, { useCallback, useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import searchFill from '@iconify/icons-eva/search-fill';
import roundFilterList from '@iconify/icons-ic/round-filter-list';
import add from '@iconify/icons-ic/add';
import { BiSync, BiCloudUpload } from 'react-icons/bi';
import { MdAssignmentInd } from 'react-icons/md';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import {
  Box,
  Tooltip,
  IconButton,
  Typography,
  InputAdornment,
  Grid,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormHelperText
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import { RootStyle, SearchStyle, DateRangeStyled } from '../Global/Styling';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { fDate } from '../../utils/formatTime';
import useProspectSelector from '../../store/Slice/Prospect/ProspectSelector';
import useGlobalSelector from '../../store/Slice/Global/GlobalSelector';
import {
  getCityData,
  getInternalSaleManager,
  getSourceListData
} from '../../store/Slice/Global/GlobalSlice';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import { getProspectList } from '../../store/Slice/Prospect/ProspectSlice';

export default function ProspectToolbar({
  filterName,
  onFilterName,
  parent,
  selectStatus,
  onFilterStatus,
  selectSource,
  onFilterSource,
  filterDatesRange,
  setFilterDatesRange,
  setOpenAssign,
  onAssignSubmit,
  openAssign,
  selected,
  alertMsg,
  setAlertMsg,
  setAssignValue,
  assignValue,
  setSelectSource,
  setFilterName,
  setSelectStatus,
  setCityListData
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { LoginData } = useLoginSelector();
  const { ProspectListDrop } = useProspectSelector();
  const { SourceDrop, InternalSaleManagerFilterDrop } = useGlobalSelector();
  const [ui, setUi] = useState({
    isOpenFilter: false,
    openSyncLeads: false,
    sourceValue: 'facebook'
  });

  useEffect(() => {
    dispatch(getProspectList({ stageId: 56 }));
    dispatch(getSourceListData());
    dispatch(getCityData({ stateId: null }));
    if (LoginData?.companyId) dispatch(getInternalSaleManager({ companyId: LoginData.companyId }));
  }, [dispatch, LoginData]);

  const resetFilters = useCallback(() => {
    setFilterDatesRange([{ startDate: null, endDate: null, key: 'selection' }]);
    setSelectSource(' ');
    setCityListData(null);
    setSelectStatus(' ');
    setFilterName('');
  }, [setFilterDatesRange, setSelectSource, setCityListData, setSelectStatus, setFilterName]);

  const renderMenuItems = (list) =>
    list?.map((item, idx) => (
      <MenuItem key={idx} value={item.value}>
        {item.label}
      </MenuItem>
    ));

  return (
    <>
      {ui.isOpenFilter && (
        <Box px={3} pt={2}>
          <Grid container spacing={3}>
            <Grid item xs>
              <Select
                fullWidth
                inputProps={{ className: `textInput customSelectBox` }}
                value={selectSource}
                onChange={onFilterSource}
              >
                <MenuItem value=" " disabled>
                  Select Source
                </MenuItem>
                {renderMenuItems(SourceDrop)}
              </Select>
            </Grid>
            <Grid item xs sx={{ position: 'relative' }}>
              <Box className="displayDateRange">
                {filterDatesRange[0].startDate ? (
                  `${fDate(filterDatesRange[0].startDate)} to ${fDate(filterDatesRange[0].endDate)}`
                ) : (
                  <Typography className="dissabledMenu">Select Date Range</Typography>
                )}
              </Box>
              <Select
                fullWidth
                inputProps={{ className: 'textInput' }}
                value=" "
                className="datesSelect"
              >
                <DateRangeStyled
                  editableDateInputs={false}
                  onChange={(item) => setFilterDatesRange([item.selection])}
                  moveRangeOnFirstSelection={false}
                  ranges={filterDatesRange}
                  className="dateRangePicker"
                />
              </Select>
            </Grid>
            <Grid item xs>
              <Select
                fullWidth
                inputProps={{ className: `textInput customSelectBox` }}
                value={selectStatus}
                onChange={onFilterStatus}
              >
                <MenuItem value=" " disabled>
                  Select Status
                </MenuItem>
                {renderMenuItems(ProspectListDrop)}
              </Select>
            </Grid>
            <Grid item xs="auto">
              <Tooltip title="Reset Filter">
                <IconButton onClick={resetFilters} size="small">
                  <FilterListOffIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </Box>
      )}

      <RootStyle sx={{ minHeight: 45, borderBottom: '1px solid #eaedf1' }}>
        <Box>
          <Tooltip title="New Prospect" arrow>
            <IconButton className="squareIconButton" onClick={() => navigate(`/${parent}/add`)}>
              <Icon icon={add} width={18} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Upload" arrow onClick={() => navigate(`/${parent}/upload`)}>
            <IconButton className="squareIconButton" sx={{ ml: 2 }}>
              <BiCloudUpload size={18} />
            </IconButton>
          </Tooltip>
          <Tooltip
            title="Sync Leads"
            arrow
            onClick={() => setUi((p) => ({ ...p, openSyncLeads: true }))}
          >
            <IconButton className="squareIconButton" sx={{ ml: 2 }}>
              <BiSync size={18} />
            </IconButton>
          </Tooltip>
          <Tooltip
            title="Assign"
            arrow
            onClick={() => {
              setOpenAssign(true);
              setAlertMsg(false);
            }}
          >
            <IconButton className="squareIconButton" sx={{ ml: 2 }} disabled={!selected?.length}>
              <MdAssignmentInd size={18} />
            </IconButton>
          </Tooltip>
        </Box>
        <Box display="flex" alignItems="center">
          <SearchStyle
            value={filterName}
            onChange={onFilterName}
            placeholder="Search Prospect"
            classes={{ root: 'searchHolder', input: 'searchInput' }}
            startAdornment={
              <InputAdornment position="start">
                <Icon icon={searchFill} style={{ color: 'gray' }} />
              </InputAdornment>
            }
          />
          <Tooltip title="Filter list">
            <IconButton onClick={() => setUi((p) => ({ ...p, isOpenFilter: !p.isOpenFilter }))}>
              <Icon icon={roundFilterList} />
            </IconButton>
          </Tooltip>
        </Box>
      </RootStyle>

      <Dialog
        open={ui.openSyncLeads}
        onClose={() => setUi((p) => ({ ...p, openSyncLeads: false }))}
        fullWidth
      >
        <DialogTitle className="popupTitle">
          <Typography variant="h6">Select Source</Typography>
          <IconButton onClick={() => setUi((p) => ({ ...p, openSyncLeads: false }))} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <RadioGroup
            value={ui.sourceValue}
            onChange={(e) => setUi((p) => ({ ...p, sourceValue: e.target.value }))}
          >
            <FormControlLabel value="facebook" control={<Radio />} label="Facebook" />
            <FormControlLabel value="callCentre" control={<Radio />} label="Call Centre" />
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setUi((p) => ({ ...p, openSyncLeads: false }))}
            variant="contained"
            className="mainBtn"
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openAssign} fullWidth>
        <DialogTitle className="popupTitle">
          <Typography variant="h6">{selected?.length} Prospect Selected</Typography>
          <IconButton
            onClick={() => {
              setOpenAssign(false);
              setAlertMsg(false);
            }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Select
            fullWidth
            inputProps={{ className: `textInput customSelectBox` }}
            value={assignValue}
            onChange={(e) => {
              setAlertMsg(false);
              setAssignValue(e.target.value);
            }}
          >
            <MenuItem value=" " disabled>
              Select Agent Name
            </MenuItem>
            {renderMenuItems(InternalSaleManagerFilterDrop)}
          </Select>
          {alertMsg && <FormHelperText error>Please select agent name</FormHelperText>}
        </DialogContent>
        <DialogActions>
          <Button onClick={onAssignSubmit} variant="contained" className="mainBtn">
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
